import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';

import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../../redux/AuthSlice/authSlice';
import { linkedInLogin, checkLinkedInConnectionStatus, clearLinkedInAuthUrl } from '../../redux/LIAuthSlice/liAuthSlice';
import DigestNavActions from '../../pages/NavPages/DailyDigestPage/DigestNavActions';
import { useNavbarActionsValue, useNavbarTitleValue } from '../../context/NavbarActionsContext';

import { Menu, X, Download, Sparkles, CreditCard } from 'lucide-react';
import CareerChampWordmark from '../Brand/CareerChampWordmark';

import BuyCreditsModal from '../Modals/BuyCreditsModal';
import CreditsUsageModal from '../Modals/CreditsUsageModal';

axios.defaults.withCredentials = true;

const PAGE_META = {
    '/':             { title: 'Jobs',           subtitle: 'Find and track opportunities' },
    '/ai-assistant': { title: 'Champ AI',        subtitle: 'Your career intelligence engine' },
    '/dailytasks':   { title: 'Daily Tasks',    subtitle: 'Your action plan for today' },
    '/daily-digest': { title: 'Daily Digest',   subtitle: 'Your AI networking agent’s picks for today' },
    '/hiringsignal': { title: 'Hiring Signals', subtitle: 'Companies actively growing their teams' },
    '/networking':   { title: 'Networking',     subtitle: 'Find warm intros to people who can refer you' },
    '/my-network':   { title: 'My Network',     subtitle: 'Your professional connections' },
    '/inbox':        { title: 'Inbox',          subtitle: '0 active conversations' },
    '/feed':         { title: 'Feed',           subtitle: 'Updates from your network and target companies' },
    '/savedjobs':    { title: 'Saved Jobs',     subtitle: "Jobs you've bookmarked" },
    '/profile':      { title: 'Profile',        subtitle: 'Manage your account and resume' },
    '/feedback':     { title: 'Feedback',       subtitle: 'Tell us how Career Champ is working for you' },
    '/support':      { title: 'Support',        subtitle: 'We typically respond within 24 hours' },
    '/subscription': { title: 'Subscription',   subtitle: 'Choose the plan that fits how you job-hunt' },
    '/credits':      { title: 'Credits & Usage', subtitle: 'See how your credits are being used' },
    '/refer':        { title: 'Refer & Earn',    subtitle: 'Invite friends · earn credits when they buy' },
};

const csvActionToType = (action, amount) => {
    if (amount > 0) return 'Credits added';
    const a = String(action || '').toLowerCase();
    if (/recruit|contact|lookup|prospeo|contactout/.test(a)) return 'Recruiter search';
    if (/ai|chat|assistant|gpt|deepseek|recompute|match/.test(a)) return 'AI Assistant';
    if (/signal|hiring|hsignal/.test(a)) return 'Hiring signal';
    return 'Other';
};

const Navbaar = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const auth = useSelector((state) => state?.auth);
    const user = auth?.user;
    const creditHistory = useSelector((state) => state?.payments?.creditHistory ?? []);
    const { linkedInAuthUrl, isLoading: liConnectLoading, isLinkedInConnected, connectionChecked } = useSelector((state) => state.liauth);

    const [isOpen, setIsOpen]                 = useState(false);
    const [isCreditModalOpen, setCreditModalOpen]   = useState(false);
    const [isCreditsUsageOpen, setCreditsUsageOpen] = useState(false);

    const navTitle = useNavbarTitleValue();
    const meta = navTitle || PAGE_META[location.pathname] || { title: '', subtitle: '' };

    const handleExportCsv = () => {
        const rows = [['Date', 'Type', 'Action', 'Source', 'Amount', 'Balance After']];
        creditHistory.forEach(item => {
            const d = new Date(item.createdAt);
            const date = isNaN(d) ? '' : d.toISOString().slice(0, 16).replace('T', ' ');
            rows.push([
                date,
                csvActionToType(item.action, item.amount),
                item.action || '',
                item.source || '',
                item.amount,
                item.balanceAfter ?? '',
            ]);
        });
        const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `credit-history-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const mobileNavItems = [
        { name: 'Jobs',           path: '/' },
        { name: 'Hiring Signals', path: '/hiringsignal' },
        { name: 'Hiring Signals (US)', path: '/hiring-signals-us' },
        { name: 'Networking',     path: '/networking' },
        { name: 'Daily Tasks',    path: '/dailytasks' },
        { name: 'Daily Digest',   path: '/daily-digest' },
        { name: 'Profile',        path: '/profile' },
    ];

    useEffect(() => { setIsOpen(false); }, [location.pathname]);

    const handleLogout = () => { dispatch(logout()); navigate('/login'); };

    const handleLIConnect = () => {
        if (user?._id) dispatch(linkedInLogin());
    };

    // Connect flow, step 1: open the Unipile hosted-auth wizard in a popup as
    // soon as a fresh auth URL arrives, then clear the URL so a remount can't
    // reopen a duplicate popup. The popup handle is kept in a ref for step 2.
    const authWindowRef = useRef(null);
    const [liConnecting, setLiConnecting] = useState(false);

    useEffect(() => {
        if (!linkedInAuthUrl) return;
        const w = window.open(
            linkedInAuthUrl,
            'LinkedIn Authentication',
            'width=600,height=700,left=200,top=100'
        );
        dispatch(clearLinkedInAuthUrl());
        if (!w) return;
        authWindowRef.current = w;
        setLiConnecting(true);
    }, [linkedInAuthUrl, dispatch]);

    // Connect flow, step 2: while connecting, wait for the popup to finish. The
    // backend callback page postMessages `cc:linkedin-auth` and closes itself —
    // that's the primary signal. Watching the popup's URL/closed state stays as a
    // fallback (older backend builds redirect the popup into the app instead).
    // Either way we then poll the backend until the account is recognized — so
    // the "Connect LinkedIn" hero disappears on its own, with no manual refresh.
    // Keyed on `liConnecting` (not the auth URL) so clearing the URL above
    // doesn't tear these timers down.
    useEffect(() => {
        if (!liConnecting) return;
        const authWindow = authWindowRef.current;
        if (!authWindow) { setLiConnecting(false); return; }

        let watchTimer, statusTimer, statusPolling = false;

        const pollStatusUntilConnected = () => {
            if (statusPolling) return;
            statusPolling = true;
            let attempts = 0;
            const tick = async () => {
                attempts += 1;
                const res = await dispatch(checkLinkedInConnectionStatus());
                if (res?.payload?.connected || attempts >= 15) {
                    clearInterval(statusTimer);
                    setLiConnecting(false);
                }
            };
            tick();
            statusTimer = setInterval(tick, 2000);
        };

        // The callback page is served by the backend, so trust its origin (and our
        // own, for the fallback path where the popup redirects into the app).
        const trustedOrigins = [window.location.origin];
        try { trustedOrigins.push(new URL(process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001').origin); } catch (_) { }

        const onAuthMessage = (event) => {
            if (event.data?.type !== 'cc:linkedin-auth' || !trustedOrigins.includes(event.origin)) return;
            try { if (!authWindow.closed) authWindow.close(); } catch (_) { }
            clearInterval(watchTimer);
            if (event.data.ok) {
                pollStatusUntilConnected();
            } else {
                setLiConnecting(false);
            }
        };
        window.addEventListener('message', onAuthMessage);

        watchTimer = setInterval(() => {
            let backOnOrigin = false;
            try {
                backOnOrigin = authWindow.location.href.includes('success=linkedin_auth_complete');
            } catch (_) {
                // Still on the Unipile (cross-origin) step — URL not readable yet.
            }
            if (backOnOrigin && !authWindow.closed) authWindow.close();
            if (authWindow.closed) {
                clearInterval(watchTimer);
                pollStatusUntilConnected();
            }
        }, 1000);

        return () => {
            window.removeEventListener('message', onAuthMessage);
            clearInterval(watchTimer);
            clearInterval(statusTimer);
        };
    }, [liConnecting, dispatch]);

    // Network-cluster quick actions (Feed / Inbox / LinkedIn) shown on My Network.
    const showNetworkActions = location.pathname === '/my-network' && isLinkedInConnected?.connected;

    // "LinkedIn not connected" badge — shown on network pages while disconnected.
    const showNotConnectedBadge = ['/my-network', '/inbox', '/feed', '/networking'].includes(location.pathname)
        && connectionChecked && !isLinkedInConnected?.connected;

    // Page-injected controls (e.g. Networking's sort / search / LinkedIn status).
    const navActions = useNavbarActionsValue();

    return (
        <>
            <nav className="fixed top-0 left-0 right-0 z-50 h-16 bg-white border-b border-[#E6E8EE] flex items-center">

                {/* ── Desktop: left brand section (matches sidebar width) ── */}
                <div className="hidden sm:flex items-center justify-center w-64 flex-shrink-0 px-3 h-full border-r border-[#E6E8EE]">
                    <button
                        onClick={() => navigate('/')}
                        className="flex items-center hover:opacity-80 transition-opacity"
                    >
                        <CareerChampWordmark className="text-[22px]" />
                    </button>
                </div>

                {/* ── Mobile: brand ──────────────────────────────────────── */}
                <button
                    onClick={() => navigate('/')}
                    className="sm:hidden flex items-center px-4 hover:opacity-80 transition-opacity"
                >
                    <CareerChampWordmark className="text-[24px]" />
                </button>

                {/* ── Desktop: page title ─────────────────────────────────── */}
                {meta.title && (
                    <div className="hidden sm:block px-6 flex-shrink-0 text-left">
                        <div className="text-[15px] font-bold tracking-tight text-[#0F172A] font-dmsans leading-tight">
                            {meta.title}
                        </div>
                        {meta.subtitle && (
                            <div className="text-[11.5px] text-[#94A3B8] font-dmsans mt-0.5 leading-tight">
                                {meta.subtitle}
                            </div>
                        )}
                    </div>
                )}

                {/* ── Right actions ───────────────────────────────────────── */}
                <div className="flex items-center gap-2 px-4 sm:px-5 ml-auto">
                    {user ? (
                        <>
                            {/* Export CSV — only on Credits page */}
                            {location.pathname === '/credits' && (
                                <button
                                    onClick={handleExportCsv}
                                    className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E6E8EE] bg-white text-[12px] font-medium font-dmsans text-[#334155] hover:bg-[#F4F5F8] transition-colors"
                                >
                                    <Download size={12} />
                                    Export CSV
                                </button>
                            )}

                            {/* Preview Tutorial wizard — only on Profile page.
                                Deep-links with ?tutorial=true; OnboardProfile picks
                                that up and opens the walkthrough. */}
                            {location.pathname === '/profile' && (
                                <button
                                    onClick={() => navigate('/profile?tutorial=true')}
                                    className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E6E8EE] bg-white text-[12px] font-medium font-dmsans text-[#334155] hover:bg-[#F4F5F8] transition-colors"
                                >
                                    <Sparkles size={12} />
                                    Preview Tutorial wizard
                                </button>
                            )}

                            {/* Regenerate + Execute — only on Daily Digest.
                                Execute spends credits, so it stays pinned in the
                                bar rather than scrolling away above a long queue. */}
                            {location.pathname === '/daily-digest' && <DigestNavActions />}

                            {/* Page-injected controls (Networking, etc.) */}
                            {navActions && (
                                <div className="hidden md:flex items-center gap-2 mr-1">
                                    {navActions}
                                </div>
                            )}

                            {/* Network quick actions — My Network page */}
                            {showNetworkActions && (
                                <div className="hidden sm:flex items-center gap-1.5 mr-1">
                                    <button
                                        onClick={() => navigate('/feed')}
                                        className="flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-semibold font-dmsans text-[#475569] hover:text-[#3F57E0] transition-colors"
                                    >
                                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M4 19.5A2.5 2.5 0 016.5 17H20" /><path strokeLinecap="round" strokeLinejoin="round" d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" /></svg>
                                        Feed
                                    </button>
                                    <button
                                        onClick={() => navigate('/inbox')}
                                        className="flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-semibold font-dmsans text-[#475569] hover:text-[#3F57E0] transition-colors"
                                    >
                                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M22 12h-6l-2 3h-4l-2-3H2" /><path strokeLinecap="round" strokeLinejoin="round" d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z" /></svg>
                                        Inbox
                                    </button>
                                    <button
                                        onClick={handleLIConnect}
                                        disabled={liConnectLoading || isLinkedInConnected?.connected}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold font-dmsans transition-colors ${
                                            isLinkedInConnected?.connected
                                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default'
                                                : 'bg-[#3F57E0] text-white hover:bg-[#3347C8]'
                                        }`}
                                    >
                                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/></svg>
                                        {isLinkedInConnected?.connected ? 'LinkedIn Connected' : liConnectLoading ? 'Connecting…' : 'Connect LinkedIn'}
                                    </button>
                                </div>
                            )}

                            {/* LinkedIn not connected — improved badge (click to connect) */}
                            {showNotConnectedBadge && (
                                <button
                                    onClick={handleLIConnect}
                                    disabled={liConnectLoading}
                                    title="Connect your LinkedIn"
                                    className="hidden sm:inline-flex items-center gap-2 pl-2.5 pr-3 py-1.5 rounded-full text-[12px] font-semibold font-dmsans bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 hover:border-amber-300 transition-colors disabled:opacity-70"
                                >
                                    <span className="relative flex h-2 w-2">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-70" />
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
                                    </span>
                                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" /><circle cx="4" cy="4" r="2" /></svg>
                                    {liConnectLoading ? 'Connecting…' : 'LinkedIn not connected'}
                                </button>
                            )}

                            {/* Buy Credits */}
                            <button
                                onClick={() => setCreditModalOpen(true)}
                                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold font-dmsans text-white bg-[#D97706] hover:bg-[#B45309] transition-all shadow-sm hover:shadow-md hover:shadow-amber-200 active:scale-[0.98]"
                            >
                                <CreditCard size={13} />
                                Buy Credits
                            </button>

                            {/* Mobile hamburger */}
                            <button
                                onClick={() => setIsOpen((p) => !p)}
                                className="sm:hidden w-8 h-8 flex items-center justify-center rounded-lg border border-[#E6E8EE] hover:bg-gray-50 transition-colors"
                                aria-label="Toggle menu"
                            >
                                {isOpen
                                    ? <X size={18} className="text-gray-600" />
                                    : <Menu size={18} className="text-gray-600" />}
                            </button>
                        </>
                    ) : (
                        <button
                            onClick={() => navigate('/login')}
                            className="text-[13px] font-semibold font-dmsans px-3.5 py-1.5 rounded-lg text-white hover:opacity-90 transition-opacity"
                            style={{ backgroundColor: '#3F57E0' }}
                        >
                            Login
                        </button>
                    )}
                </div>
            </nav>

            {/* ── Mobile dropdown ─────────────────────────────────────────── */}
            {isOpen && (
                <div className="fixed top-16 left-0 right-0 bg-white border-t border-[#E6E8EE] shadow-lg sm:hidden z-40 p-3 flex flex-col gap-1">
                    {mobileNavItems.map((item) => {
                        const active = location.pathname === item.path;
                        return (
                            <button
                                key={item.path}
                                onClick={() => { navigate(item.path); setIsOpen(false); }}
                                className={`w-full text-left px-3 py-2.5 rounded-lg text-[13px] font-medium font-dmsans transition-colors
                                    ${active
                                        ? 'bg-[#EEF1FE] text-[#3F57E0] font-semibold'
                                        : 'text-[#334155] hover:bg-[#F4F5F8]'}`}
                            >
                                {item.name}
                            </button>
                        );
                    })}

                    {user && (
                        <>
                            <div className="border-t border-[#E6E8EE] my-1" />
                            <div className="px-3 py-2 flex items-center justify-between">
                                <button
                                    type="button"
                                    onClick={() => { setCreditsUsageOpen(true); setIsOpen(false); }}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#E6E8EE] bg-[#EEF1FE]"
                                >
                                    <span className="text-[10px] text-[#94A3B8] font-semibold font-dmsans">Credits</span>
                                    <span className="text-[11px] font-bold font-dmsans text-[#3F57E0]">{user?.creditsBalance ?? 0}</span>
                                </button>
                                <button
                                    onClick={() => { setCreditModalOpen(true); setIsOpen(false); }}
                                    className="text-[12px] font-semibold font-dmsans text-white px-3 py-1.5 rounded-lg hover:opacity-90 transition-opacity"
                                    style={{ backgroundColor: '#3F57E0' }}
                                >
                                    Buy Credits
                                </button>
                            </div>
                            <button
                                onClick={() => { handleLogout(); setIsOpen(false); }}
                                className="w-full text-left px-3 py-2.5 rounded-lg text-[13px] font-medium font-dmsans text-red-500 hover:bg-red-50 transition-colors"
                            >
                                Log out
                            </button>
                        </>
                    )}
                </div>
            )}

            <BuyCreditsModal
                isOpen={isCreditModalOpen}
                onClose={() => setCreditModalOpen(false)}
            />
            <CreditsUsageModal
                isOpen={isCreditsUsageOpen}
                onClose={() => setCreditsUsageOpen(false)}
            />
        </>
    );
};

export default Navbaar;
